import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { BadgeCheck, Check, Download, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { eur, fmtDate, normalizeList } from "@/lib/invoice";
import { trackMetaEvent } from "@/lib/metaPixel";

const TIER_LABELS = { starter: "Starter", pro: "Pro", enterprise: "Entreprise" };
const TIER_PRICES = { starter: 9, pro: 19, enterprise: 49 };

export default function SubscriptionPage() {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [ppConfig, setPpConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifyingTier, setVerifyingTier] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [subInvoices, setSubInvoices] = useState([]);

  const load = useCallback(async () => {
    const [p, s, c, inv] = await Promise.allSettled([
      api.get("/plans"),
      api.get("/subscription"),
      api.get("/paypal/config"),
      api.get("/subscription/invoices"),
    ]);
    if (p.status === "fulfilled") setPlans(p.value.data?.plans || []);
    if (s.status === "fulfilled") setSub(s.value.data);
    if (c.status === "fulfilled") setPpConfig(c.value.data);
    if (inv.status === "fulfilled") setSubInvoices(normalizeList(inv.value.data));
    setLoading(false);
  }, []);

  const downloadSubInvoice = async (path) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = path.split("/").pop() || "facture.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  useEffect(() => { load(); }, [load]);

  const active = sub && !["none", "trial", "cancelled", "canceled", "expired"].includes((sub.status || "none").toLowerCase());
  // Multi-plans uniquement : {plan_ids: {starter, pro, enterprise}} — pas de repli sur l'ancien plan_id,
  // car l'ID historique renvoyé par l'API correspond au plan "Entreprise" côté PayPal (risque de surfacturation).
  const planIds = ppConfig?.plan_ids || {};

  const handleApprove = (tier) => async (data) => {
    setVerifyingTier(tier);
    try {
      await api.post("/paypal/verify", { subscription_id: data.subscriptionID, tier });
      trackMetaEvent("Subscribe", { value: TIER_PRICES[tier], currency: "EUR", content_name: TIER_LABELS[tier] || tier });
      toast.success(`Abonnement ${TIER_LABELS[tier] || tier} activé — bienvenue !`, { duration: 6000 });
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Vérification PayPal impossible");
    } finally {
      setVerifyingTier(null);
    }
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Abonnement résilié");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Résiliation impossible");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-32" data-testid="subscription-loading">
        <div className="h-10 w-10 rounded-full border-2 border-[#2563EB] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="subscription-page" className="max-w-5xl">
      <p className="overline-label mb-2">Abonnement</p>
      <h1 className="font-display font-light tracking-tighter text-3xl md:text-4xl mb-8">Votre formule</h1>

      {/* Statut actuel */}
      <div className="rounded-2xl border border-white/10 bg-[#0A0E17] p-6 md:p-8 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="subscription-status-panel">
        <div className="flex items-center gap-4">
          <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl border ${active ? "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/40" : "bg-white/5 text-zinc-400 border-white/10"}`}>
            <BadgeCheck className="h-6 w-6" />
          </span>
          <div>
            <p className="overline-label">Statut</p>
            <p className="font-display text-xl capitalize mt-1" data-testid="subscription-status-value">
              {active ? `${sub.plan || sub.tier || "Pro"} — actif` : "Aucun abonnement actif"}
            </p>
            {sub?.subscription_id && (
              <p className="font-mono text-xs text-zinc-500 mt-1">{sub.subscription_id}</p>
            )}
          </div>
        </div>
        {active && (
          <button
            onClick={cancel}
            disabled={cancelling}
            data-testid="cancel-subscription-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#FF3B30]/40 text-[#FF3B30] px-5 py-2.5 text-sm hover:bg-[#FF3B30]/10 transition-colors duration-200 disabled:opacity-50"
          >
            {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
            Résilier
          </button>
        )}
      </div>

      {/* Bandeau essai gratuit (renvoyé par le backend : sub.trial = {used, limit, remaining}) */}
      {!active && sub?.trial && (() => {
        const limit = Number(sub.trial.limit) || 3;
        const remaining = sub.trial.remaining ?? Math.max(0, limit - (Number(sub.trial.used) || 0));
        const over = remaining <= 0;
        return (
          <div
            className={`rounded-2xl border p-6 mb-8 ${over ? "border-[#FF3B30]/40 bg-[#FF3B30]/5" : "border-[#00D4FF]/30 bg-[#00D4FF]/5"}`}
            data-testid="trial-banner"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <p className="font-display text-lg" data-testid="trial-banner-title">
                {over ? "Essai gratuit terminé" : `Essai gratuit — ${remaining} facture${remaining > 1 ? "s" : ""} offerte${remaining > 1 ? "s" : ""}`}
              </p>
              <p className="font-mono text-xs text-zinc-400" data-testid="trial-banner-count">
                {Number(sub.trial.used) || 0} / {limit} utilisées
              </p>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-3">
              <div
                className={`h-full rounded-full ${over ? "bg-[#FF3B30]" : "bg-gradient-to-r from-[#2563EB] to-[#00D4FF]"}`}
                style={{ width: `${Math.min(100, ((Number(sub.trial.used) || 0) / limit) * 100)}%` }}
              />
            </div>
            <p className="text-sm text-zinc-400">
              {over
                ? "Vous avez utilisé vos factures offertes. Choisissez une formule ci-dessous pour continuer."
                : "Testez toute la puissance de FacturXpress : import PDF + IA, Factur-X, octroi de mer. Sans carte bancaire."}
            </p>
          </div>
        );
      })()}

      {/* Plans + paiement PayPal par formule — un SEUL PayPalScriptProvider pour toute la grille */}
      {(() => {
        const grid = (
          <div className="grid md:grid-cols-3 gap-4 mb-4" data-testid="plans-grid">
            {plans.map((p, i) => {
              const isPro = p.id === "pro";
              const planId = planIds[p.id];
              const isCurrent = active && (sub.plan || sub.tier || "").toLowerCase() === p.id;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  data-testid={`sub-plan-${p.id}`}
                  className={`rounded-2xl p-6 bg-[#0A0E17] border flex flex-col ${isPro ? "border-[#2563EB]/50" : "border-white/10"}`}
                >
                  <h3 className="font-display text-lg">{p.name}</h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{p.quota}</p>
                  <p className="mt-4">
                    <span className="font-display text-4xl font-light">{p.price}€</span>
                    <span className="text-zinc-500 text-sm"> / {p.period}</span>
                  </p>
                  <ul className="mt-5 space-y-2 flex-1">
                    {(p.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
                        <Check className="h-4 w-4 mt-0.5 text-[#34C759] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6">
                    {isCurrent ? (
                      <span data-testid={`current-plan-badge-${p.id}`} className="inline-flex items-center gap-2 rounded-full bg-[#34C759]/15 border border-[#34C759]/40 text-[#34C759] px-4 py-2 text-xs font-mono uppercase tracking-wider">
                        <BadgeCheck className="h-3.5 w-3.5" /> Formule actuelle
                      </span>
                    ) : !active && planId && ppConfig?.client_id ? (
                      verifyingTier === p.id ? (
                        <div className="flex items-center gap-3 text-sm text-zinc-300 py-3" data-testid={`paypal-verifying-${p.id}`}>
                          <Loader2 className="h-4 w-4 animate-spin text-[#00D4FF]" />
                          Activation en cours…
                        </div>
                      ) : (
                        <div data-testid={`paypal-subscribe-${p.id}`}>
                          <PayPalButtons
                            style={{ layout: "vertical", shape: "pill", color: isPro ? "gold" : "blue", label: "subscribe" }}
                            createSubscription={(data, actions) => {
                              trackMetaEvent("InitiateCheckout", { content_name: TIER_LABELS[p.id], value: TIER_PRICES[p.id], currency: "EUR" });
                              return actions.subscription.create({ plan_id: planId });
                            }}
                            onApprove={handleApprove(p.id)}
                            onCancel={() => toast.info("Paiement annulé")}
                            onError={() => toast.error("Erreur PayPal — réessayez")}
                          />
                        </div>
                      )
                    ) : !active ? (
                      <span className="text-xs text-zinc-500" data-testid={`plan-unavailable-${p.id}`}>
                        Paiement en ligne bientôt disponible
                      </span>
                    ) : null}
                  </div>
                </motion.div>
              );
            })}
          </div>
        );
        return ppConfig?.client_id ? (
          <PayPalScriptProvider
            options={{ clientId: ppConfig.client_id, vault: true, intent: "subscription", currency: "EUR" }}
          >
            {grid}
          </PayPalScriptProvider>
        ) : grid;
      })()}
      <p className="text-xs text-zinc-600 mb-8">
        Paiement sécurisé par PayPal — débit mensuel automatique, résiliable à tout moment. En souscrivant, vous acceptez les{" "}
        <a href="https://factur-convert.emergent.host/api/legal/cgu" target="_blank" rel="noreferrer" className="underline hover:text-zinc-400">CGU</a>.
      </p>

      {/* Historique des factures d'abonnement (alimenté par GET /api/subscription/invoices) */}
      {subInvoices.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-[#0A0E17] overflow-hidden mb-8" data-testid="subscription-invoices">
          <p className="overline-label px-6 pt-5 pb-4">Historique de facturation</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-white/10 text-left">
                  <th className="px-6 py-3 overline-label">Numéro</th>
                  <th className="px-6 py-3 overline-label">Date</th>
                  <th className="px-6 py-3 overline-label">Formule</th>
                  <th className="px-6 py-3 overline-label text-right">Montant</th>
                  <th className="px-6 py-3 overline-label text-right">PDF</th>
                </tr>
              </thead>
              <tbody>
                {subInvoices.map((inv, i) => (
                  <tr key={inv.numero || i} data-testid={`sub-invoice-${inv.numero || i}`} className="border-t border-white/5">
                    <td className="px-6 py-3.5 font-mono text-[#00D4FF]">{inv.numero || "—"}</td>
                    <td className="px-6 py-3.5 text-zinc-400">{fmtDate(inv.date)}</td>
                    <td className="px-6 py-3.5 text-zinc-300 capitalize">{TIER_LABELS[inv.tier] || inv.tier || "—"}</td>
                    <td className="px-6 py-3.5 text-right font-mono">{eur(inv.amount_eur ?? inv.amount)}</td>
                    <td className="px-6 py-3.5 text-right">
                      {inv.file_path ? (
                        <button
                          onClick={() => downloadSubInvoice(inv.file_path)}
                          data-testid={`download-sub-invoice-${inv.numero || i}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs hover:border-[#00D4FF] hover:text-[#00D4FF] transition-colors duration-200"
                        >
                          <Download className="h-3.5 w-3.5" /> PDF
                        </button>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-sm text-zinc-500">
        Une question sur la facturation ? <Link to="/app/parametres" data-testid="subscription-settings-link" className="text-[#00D4FF] hover:underline">Voir vos paramètres</Link>
      </p>
    </div>
  );
}
